import React from 'react';
import { Building2, Target, Eye, Heart, Award, Users, TrendingUp, Shield } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Card, CardContent } from '../components/ui/card';
import { useSiteData } from '../contexts/SiteDataContext';

const JOHN_DEERE_IMAGE = 'https://customer-assets.emergentagent.com/job_5793c14a-9e5d-45df-be07-4fd47ebae7d7/artifacts/mwg33gsz_Captura%20de%20tela%202026-04-24%20153757.png';
const OLD_UNSPLASH = 'https://images.unsplash.com/photo-1669902081596-18497f52659e';

const About = () => {
  const { settings, pageContents } = useSiteData();
  const about = pageContents.about_company?.metadata || {};
  const aboutTitle = pageContents.about_company?.title || 'Sobre a TerraHydro';
  const aboutSubtitle = pageContents.about_company?.subtitle || settings.siteSlogan || 'A peça certa, na aplicação correta.';
  const aboutContent = pageContents.about_company?.content || 'A TerraHydro Soluções Hidráulicas é especializada na distribuição de componentes hidráulicos de alta performance para os setores agrícola e industrial.';
  const founded = about.founded || 2013;
  const mission = about.mission || 'Fornecer componentes hidráulicos de excelência com suporte técnico especializado.';
  const vision = about.vision || 'Ser a referência nacional em distribuição técnica de componentes hidráulicos.';
  const values = about.values || ['Expertise técnica', 'Qualidade garantida', 'Atendimento consultivo', 'Agilidade na entrega', 'Transparência comercial'];
  // Use John Deere image - override old Unsplash/Komatsu if still in DB
  const dbImage = about.imageUrl;
  const imageUrl = (!dbImage || dbImage.includes('unsplash.com')) ? JOHN_DEERE_IMAGE : dbImage;
  const stats = about.stats || [
    { value: "400+", label: "Produtos no Catálogo" },
    { value: "15+", label: "Anos de Experiência" },
    { value: "98%", label: "Satisfação" },
    { value: "24h", label: "Tempo Médio de Resposta" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#055509] to-[#0a7a0f] text-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{aboutTitle}</h1>
            <p className="text-2xl text-gray-200 leading-relaxed">
              {aboutSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-[#FFB025] bg-opacity-20 rounded-full border border-[#FFB025] mb-6">
                <span className="text-[#055509] font-semibold">Nossa História</span>
              </div>
              <h2 className="text-4xl font-bold text-[#055509] mb-6">
                Expertise em Hidráulica desde {founded}
              </h2>
              <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
                <p>{aboutContent}</p>
                <p>
                  Ao longo dos anos, desenvolvemos um profundo conhecimento técnico que nos permite não apenas fornecer componentes, mas atuar como verdadeiros consultores técnicos. Entendemos que cada aplicação é única e requer análise cuidadosa para garantir performance máxima e durabilidade.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src={imageUrl}
                alt="Equipe técnica TerraHydro"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#FFB025] text-[#055509] p-6 rounded-xl shadow-2xl">
                <div className="text-4xl font-bold mb-1">15+</div>
                <div className="font-semibold">Anos de Mercado</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Mission */}
            <Card className="border-2 border-[#055509] hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-[#055509] rounded-full flex items-center justify-center">
                  <Target className="text-[#FFB025]" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#055509] mb-4">Missão</h3>
                <p className="text-gray-700 leading-relaxed">
                  {mission}
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="border-2 border-[#FFB025] hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-[#FFB025] rounded-full flex items-center justify-center">
                  <Eye className="text-[#055509]" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#055509] mb-4">Visão</h3>
                <p className="text-gray-700 leading-relaxed">
                  {vision}
                </p>
              </CardContent>
            </Card>

            {/* Values */}
            <Card className="border-2 border-[#055509] hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-[#055509] rounded-full flex items-center justify-center">
                  <Heart className="text-[#FFB025]" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#055509] mb-4">Valores</h3>
                <ul className="text-gray-700 space-y-2 text-left">
                  {values.map((value, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-[#FFB025] rounded-full"></div>
                      <span>{value}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gradient-to-br from-[#055509] to-[#0a7a0f] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Números que Comprovam</h2>
            <p className="text-xl text-gray-200">
              Nossa dedicação à excelência refletida em resultados
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-5xl font-bold text-[#FFB025] mb-2">{stat.value}</div>
                <div className="text-gray-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#055509] mb-4">
              Por Que Somos Diferentes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              O que nos torna a escolha preferida de oficinas e profissionais
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center hover:shadow-xl transition-shadow border-2 border-gray-100">
              <CardContent className="p-6">
                <div className="w-14 h-14 mx-auto mb-4 bg-[#055509] rounded-lg flex items-center justify-center">
                  <Award className="text-[#FFB025]" size={28} />
                </div>
                <h3 className="font-bold text-[#055509] mb-2">Qualidade Certificada</h3>
                <p className="text-sm text-gray-600">
                  Apenas componentes originais e certificados
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-2 border-gray-100">
              <CardContent className="p-6">
                <div className="w-14 h-14 mx-auto mb-4 bg-[#055509] rounded-lg flex items-center justify-center">
                  <Users className="text-[#FFB025]" size={28} />
                </div>
                <h3 className="font-bold text-[#055509] mb-2">Equipe Especializada</h3>
                <p className="text-sm text-gray-600">
                  Técnicos com anos de experiência prática
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-2 border-gray-100">
              <CardContent className="p-6">
                <div className="w-14 h-14 mx-auto mb-4 bg-[#055509] rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-[#FFB025]" size={28} />
                </div>
                <h3 className="font-bold text-[#055509] mb-2">Estoque Amplo</h3>
                <p className="text-sm text-gray-600">
                  Pronta entrega para reduzir seu tempo de parada
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-2 border-gray-100">
              <CardContent className="p-6">
                <div className="w-14 h-14 mx-auto mb-4 bg-[#055509] rounded-lg flex items-center justify-center">
                  <Shield className="text-[#FFB025]" size={28} />
                </div>
                <h3 className="font-bold text-[#055509] mb-2">Garantia Total</h3>
                <p className="text-sm text-gray-600">
                  Aplicação correta ou devolvemos seu dinheiro
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default About;
